import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { trackEvent } from "../../../utils/analytics";
import { type OpenShiftRequest, type Recommendation, RecommendationType, type ReplaceShiftRequest, type RequestExtraData } from "../../../utils/models";
import { useRequestUtils } from "../../RequestData/Provider/useRequestUtils";
import type { TabType } from "../../StaffingChanges/StaffingChanges";

export const useRecommendations = () => {
	const { staffRequests } = useRequestsContext();
	const { recomendationFromRequests, approveRequest } = useRequestUtils();
	const [staffingChangesTab, setStaffingChangesTab] = useState<TabType>();
	const [selectedDate, setSelectedDate] = useState<DateTime | undefined>(DateTime.now());
	const [selectedRequest, setSelectedRequest] = useState<OpenShiftRequest | ReplaceShiftRequest | undefined>();
	const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | undefined>();

	const onApprove = useCallback(
		(recommendation: Recommendation): void => {
			trackEvent("recommendation_approved", recommendation);
			setSelectedDate(recommendation.date);
			setSelectedRecommendation(recommendation);

			switch (recommendation.type) {
				case RecommendationType.PostShift: {
					setStaffingChangesTab("create-shift");
					return;
				}
				case RecommendationType.ApproveRequest: {
					const requestId: string = (recommendation.extra_data as RequestExtraData).data;
					const request = staffRequests.find((request) => request.id === requestId);
					setSelectedRequest(request);
					return;
				}
				case RecommendationType.ApproveReplacementRequest: {
					const requestId: string = (recommendation.extra_data as RequestExtraData).data;
					const request = staffRequests.find((request) => request.id === requestId);
					setSelectedRequest(request);
					return;
				}
				case RecommendationType.RemoveStaff: {
					setStaffingChangesTab("remove-nurse");
					return;
				}
			}
		},
		[staffRequests],
	);
	const onClose = useCallback((): void => {
		setSelectedDate(undefined);
		setStaffingChangesTab(undefined);
		setSelectedRequest(undefined);
		setSelectedRecommendation(undefined);
	}, []);

	const onComplete = useCallback(
		async (idx?: number): Promise<void> => {
			trackEvent("recommendation_completed");

			if (selectedRequest && idx != null) {
				const rec = recomendationFromRequests(selectedRequest as OpenShiftRequest);

				if (rec.suggestions[idx]) {
					await approveRequest(selectedRequest, rec.suggestions[idx].otherNurseId ?? rec.suggestions[idx].nurseId);
				}
			}

			onClose();
		},
		[selectedRequest, recomendationFromRequests, approveRequest, onClose],
	);

	return {
		selectedDate,
		staffingChangesTab,
		selectedRequest,
		selectedRecommendation,
		onApprove,
		onComplete,
		onClose,
	};
};
